import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, Calendar, CreditCard, Settings as Cog, BarChart3, Bug, MailWarning, Send, CheckSquare } from "lucide-react";
import { api } from "../lib/api";
import { getSite, setSite, isSiteLocked } from "../lib/site";
import AeronaIntegratorSettings from "../components/admin/AeronaIntegratorSettings";

function StatCard({ label, value, suffix, testId }) {
  return (
    <div className="ws-card p-5" data-testid={testId || `stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-xs uppercase tracking-wider text-[#5C5C5C]">{label}</div>
      <div className="text-3xl font-extrabold mt-1 text-[#0466ff]" style={{ fontFamily: "Manrope" }}>
        {suffix === "€" && suffix}{value}
      </div>
    </div>
  );
}

function BookingsTable({ bookings }) {
  if (!bookings.length) return <div className="text-[#5C5C5C] py-8 text-center">No bookings yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="bookings-table">
        <thead className="text-left text-[#5C5C5C] text-xs uppercase tracking-wider">
          <tr className="border-b border-[#E5E5E5]">
            <th className="py-3 pr-3">When</th>
            <th className="py-3 pr-3">Patient</th>
            <th className="py-3 pr-3">Service</th>
            <th className="py-3 pr-3">Clinic</th>
            <th className="py-3 pr-3">Dentist</th>
            <th className="py-3 pr-3">Total</th>
            <th className="py-3 pr-3">Payment</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => (
            <tr key={b.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`booking-row-${b.id}`}>
              <td className="py-3 pr-3">
                {new Date(b.appointment_time).toLocaleString("en-IE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </td>
              <td className="py-3 pr-3">
                <div className="font-semibold">{b.first_name} {b.last_name}</div>
                <div className="text-xs text-[#5C5C5C]">{b.email} · {b.phone}</div>
              </td>
              <td className="py-3 pr-3">{b.service_name}</td>
              <td className="py-3 pr-3">{b.practice_label || b.practice_name || "—"}</td>
              <td className="py-3 pr-3">{b.clinician_id_aerona || b.clinician_name || "—"}</td>
              <td className="py-3 pr-3">€{(b.total || 0).toFixed(2)}</td>
              <td className="py-3 pr-3">
                {b.payment_status === "paid" ? (
                  <span className="ws-chip" style={{ background: "#E8F0FE", color: "#2E664F" }}>Paid online</span>
                ) : b.payment_status === "not_required" ? (
                  <span className="ws-chip">Free</span>
                ) : b.pay_online ? (
                  <span className="ws-chip" style={{ background: "#FFF4EE", color: "#C85A41" }}>Pending</span>
                ) : (
                  <span className="ws-chip">Pay at clinic</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTable() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.adminPayments().then(setItems); }, []);
  if (!items.length) return <div className="text-[#5C5C5C] py-8 text-center">No payment activity yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="payments-table">
        <thead className="text-left text-[#5C5C5C] text-xs uppercase tracking-wider">
          <tr className="border-b border-[#E5E5E5]">
            <th className="py-3 pr-3">When</th>
            <th className="py-3 pr-3">Session</th>
            <th className="py-3 pr-3">Amount</th>
            <th className="py-3 pr-3">Status</th>
            <th className="py-3 pr-3">Booking</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-[#F0F0F0]" data-testid={`payment-row-${p.id}`}>
              <td className="py-3 pr-3">{new Date(p.created_at).toLocaleString("en-IE")}</td>
              <td className="py-3 pr-3 font-mono text-xs">{(p.session_id || "").slice(0, 16)}…</td>
              <td className="py-3 pr-3">€{(p.amount || 0).toFixed(2)}</td>
              <td className="py-3 pr-3">
                <span className={`ws-chip ${p.payment_status === "paid" ? "" : ""}`}
                  style={p.payment_status === "paid" ? { background: "#E8F0FE", color: "#2E664F" } : { background: "#FFF4EE", color: "#C85A41" }}>
                  {p.payment_status}
                </span>
              </td>
              <td className="py-3 pr-3 font-mono text-xs">{(p.booking_id || "").slice(0, 8)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BookingsByDate({ bookings }) {
  // Default to today (yyyy-mm-dd in LOCAL time so it matches what we render)
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const [date, setDate] = useState(todayStr);

  const filtered = bookings.filter((b) => {
    if (!b.appointment_time) return false;
    const d = new Date(b.appointment_time);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return key === date;
  });

  const prettyDate = (() => {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  })();

  return (
    <div data-testid="bookings-by-date-pane">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>
            Show bookings for
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="ws-input"
            data-testid="bookings-date-picker"
          />
        </div>
        <div className="text-sm text-[#5C5C5C] pb-2.5">
          {prettyDate} — <strong className="text-[#1A1A1A]">{filtered.length}</strong> booking{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>
      <BookingsTable bookings={filtered} />
    </div>
  );
}

function StatisticsPane({ bookings }) {
  // yyyy-mm-dd for the LOCAL calendar day (matches the date-picker value).
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return fmt(d); };
  const yesterdayStr = daysAgo(1);
  const todayStr = fmt(new Date());
  const [range, setRange] = useState("day"); // day | 7d | 30d | custom
  const [date, setDate] = useState(yesterdayStr);
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(todayStr);
  const [mode, setMode] = useState("created");

  // Derive [start, end] inclusive yyyy-mm-dd bounds from the current range.
  const { startStr, endStr, prettyRange } = (() => {
    if (range === "day") {
      const [y, m, d] = date.split("-").map(Number);
      const p = new Date(y, m - 1, d).toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      return { startStr: date, endStr: date, prettyRange: p };
    }
    if (range === "7d") return { startStr: daysAgo(6), endStr: todayStr, prettyRange: "Last 7 days" };
    if (range === "30d") return { startStr: daysAgo(29), endStr: todayStr, prettyRange: "Last 30 days" };
    // custom
    const from = fromDate <= toDate ? fromDate : toDate;
    const to = fromDate <= toDate ? toDate : fromDate;
    return { startStr: from, endStr: to, prettyRange: `${from} → ${to}` };
  })();

  const counts = new Map();
  const paidCounts = new Map();
  let total = 0;
  let totalPaid = 0;
  // Only count REAL bookings — customers who actually confirmed with us.
  // Step-4 draft lead-captures (is_draft=true / payment_status=pending)
  // and abandoned Stripe/PayPal sessions must NOT inflate the counts.
  const REAL_STATUS = new Set(["paid", "not_required", "manual_completed"]);
  bookings.forEach((b) => {
    if (b.is_draft) return;
    if (!REAL_STATUS.has(b.payment_status)) return;
    const ts = mode === "created" ? b.created_at : b.appointment_time;
    if (!ts) return;
    const d = new Date(ts);
    const day = fmt(d);
    if (day < startStr || day > endStr) return;
    const name = b.service_name || "Unknown";
    counts.set(name, (counts.get(name) || 0) + 1);
    total += 1;
    if (b.payment_status === "paid") {
      paidCounts.set(name, (paidCounts.get(name) || 0) + 1);
      totalPaid += 1;
    }
  });
  const rows = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  return (
    <div data-testid="statistics-pane">
      <h1 className="text-3xl font-extrabold mb-1" style={{ fontFamily: "Manrope" }}>Statistics</h1>
      <p className="text-[#5C5C5C] mb-6">Treatment volume + paid conversions. Pick a single day or a range.</p>

      <div className="ws-card p-5">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Show</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="ws-input" data-testid="stats-mode-select">
              <option value="created">Bookings made</option>
              <option value="appointment">Appointments scheduled</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Range</label>
            <select value={range} onChange={(e) => setRange(e.target.value)} className="ws-input" data-testid="stats-range-select">
              <option value="day">Single day</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {range === "day" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Date</label>
              <input type="date" value={date} max={todayStr} onChange={(e) => setDate(e.target.value)} className="ws-input" data-testid="stats-date-picker" />
            </div>
          )}
          {range === "custom" && (
            <>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>From</label>
                <input type="date" value={fromDate} max={todayStr} onChange={(e) => setFromDate(e.target.value)} className="ws-input" data-testid="stats-from-picker" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>To</label>
                <input type="date" value={toDate} max={todayStr} onChange={(e) => setToDate(e.target.value)} className="ws-input" data-testid="stats-to-picker" />
              </div>
            </>
          )}
          <div className="text-sm text-[#5C5C5C] pb-2.5">
            {prettyRange} — <strong className="text-[#1A1A1A]">{total}</strong> booking{total !== 1 ? "s" : ""}, <strong className="text-[#0F8F4A]">{totalPaid}</strong> paid
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-[#5C5C5C] py-8 text-center" data-testid="stats-empty">No bookings in this range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="statistics-table">
              <thead className="text-left text-[#5C5C5C] text-xs uppercase tracking-wider">
                <tr className="border-b border-[#E5E5E5]">
                  <th className="py-3 pr-3">Treatment Name</th>
                  <th className="py-3 pr-3 text-right">No. of Bookings</th>
                  <th className="py-3 pr-3 text-right">No. of Payments</th>
                  <th className="py-3 pr-3 text-right">% Paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([name, n]) => {
                  const paid = paidCounts.get(name) || 0;
                  const pct = n > 0 ? Math.round((paid / n) * 100) : 0;
                  return (
                    <tr key={name} className="border-b border-[#F0F0F0]" data-testid={`stats-row-${name.slice(0, 20).replace(/\s+/g, "-")}`}>
                      <td className="py-3 pr-3 font-medium text-[#1A1A1A]">{name}</td>
                      <td className="py-3 pr-3 text-right font-bold text-[#0466ff]">{n}</td>
                      <td className="py-3 pr-3 text-right font-bold text-[#0F8F4A]" data-testid={`stats-paid-${name.slice(0, 20).replace(/\s+/g, "-")}`}>
                        {paid > 0 ? paid : "—"}
                      </td>
                      <td className="py-3 pr-3 text-right font-bold text-[#5C5C5C]" data-testid={`stats-pct-${name.slice(0, 20).replace(/\s+/g, "-")}`}>
                        {paid > 0 ? `${pct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-[#FAFAFA]">
                  <td className="py-3 pr-3 font-bold uppercase text-xs tracking-wider text-[#5C5C5C]">Total</td>
                  <td className="py-3 pr-3 text-right font-extrabold text-[#1A1A1A]">{total}</td>
                  <td className="py-3 pr-3 text-right font-extrabold text-[#0F8F4A]" data-testid="stats-paid-total">{totalPaid}</td>
                  <td className="py-3 pr-3 text-right font-extrabold text-[#1A1A1A]" data-testid="stats-pct-total">
                    {total > 0 ? `${Math.round((totalPaid / total) * 100)}%` : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AbandonedPane({ siteKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showRecovered, setShowRecovered] = useState(false);
  const [emailsEnabled, setEmailsEnabled] = useState(true);
  const [togglingEmails, setTogglingEmails] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [res, status] = await Promise.all([
        api.adminAbandonedBookings({ include_recovered: showRecovered }),
        api.adminAbandonedEmailsStatus(),
      ]);
      setData(res);
      setEmailsEnabled(!!status?.enabled);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmails = async (next) => {
    setTogglingEmails(true);
    try {
      const r = await api.adminSetAbandonedEmailsEnabled(next);
      setEmailsEnabled(!!r?.enabled);
      toast.success(r?.enabled ? "Abandoned emails switched ON" : "Abandoned emails switched OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update setting");
    } finally {
      setTogglingEmails(false);
    }
  };

  const resend = async (id) => {
    setBusyId(id);
    try {
      const r = await api.adminResendAbandoned(id);
      if (r?.ok) toast.success("Recovery email re-sent");
      else toast.error(r?.result?.message || "Send failed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusyId(null); }
  };

  const markCompleted = async (id) => {
    if (!window.confirm("Mark this abandoned booking as manually handled? The scheduler will stop chasing it.")) return;
    setBusyId(id);
    try {
      await api.adminMarkAbandonedCompleted(id);
      toast.success("Marked as manually completed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusyId(null); }
  };

  useEffect(() => { refresh();  }, [siteKey, showRecovered]);

  const rows = data?.rows || [];
  const stats = data?.stats || {};
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div data-testid="abandoned-pane">
      <h1 className="text-3xl font-extrabold mb-1" style={{ fontFamily: "Manrope" }}>Abandoned Bookings</h1>
      <p className="text-[#5C5C5C] mb-4">
        Customers who started a booking on {siteKey === "clinic" ? "White Smile Clinic" : "White Smile Dental"} but never completed payment.
        Recovery emails fire automatically after 30 min and again 24 h later.
        Customers who already have a completed booking (same email or phone) are automatically hidden and never chased.
      </p>

      <div
        className={`flex items-center justify-between gap-3 rounded-lg border-2 px-4 py-3 mb-4 ${
          emailsEnabled ? "border-[#BDEACB] bg-[#E6F8EE]" : "border-[#FCA5A5] bg-[#FEF2F2]"
        }`}
        data-testid="abandoned-emails-toggle-card"
      >
        <div className="min-w-0">
          <div className="text-sm font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope" }}>
            Automated abandoned-cart emails: {emailsEnabled ? "ON" : "OFF"}
          </div>
          <div className="text-[12px] text-[#5C5C5C] leading-snug mt-0.5">
            {emailsEnabled
              ? "Reminder emails send automatically 30 min and 24 h after a customer abandons the booking wizard."
              : "No automated emails are being sent for this site. Manual \"Resend\" buttons below still work."}
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleEmails(!emailsEnabled)}
          disabled={togglingEmails}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            emailsEnabled ? "bg-[#0F8F4A]" : "bg-[#9CA3AF]"
          }`}
          data-testid="abandoned-emails-toggle-switch"
          aria-pressed={emailsEnabled}
          aria-label="Toggle automated abandoned emails"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              emailsEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none" data-testid="show-recovered-toggle">
          <input
            type="checkbox"
            checked={showRecovered}
            onChange={(e) => setShowRecovered(e.target.checked)}
            className="w-4 h-4 accent-[#0466ff]"
            data-testid="show-recovered-checkbox"
          />
          Show recovered (customer already booked with us)
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total abandoned" value={stats.total_abandoned ?? 0} testId="stat-abandoned-total" />
        <StatCard label="Emails sent" value={stats.emailed ?? 0} testId="stat-abandoned-emailed" />
        <StatCard label="Recovered" value={stats.recovered_total ?? 0} testId="stat-abandoned-recovered" />
        <StatCard label="Conversion" value={`${stats.conversion_rate_pct ?? 0}%`} testId="stat-abandoned-conversion" />
      </div>

      {loading ? (
        <div className="text-[#5C5C5C]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="ws-card p-8 text-center text-[#5C5C5C]" data-testid="abandoned-empty">
          No abandoned bookings for this site yet.
        </div>
      ) : (
        <div className="ws-card overflow-hidden" data-testid="abandoned-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F7FA] text-left text-[#5C5C5C] uppercase text-[11px] tracking-wide">
                <tr>
                  <th className="px-3 py-2">Started</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Email 1</th>
                  <th className="px-3 py-2">Email 2</th>
                  <th className="px-3 py-2">Recovered</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0F0]">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`abandoned-row-${r.id}`} className="hover:bg-[#FAFCFF]">
                    <td className="px-3 py-2 text-[#5C5C5C] whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2 font-semibold text-[#1A1A1A]">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-2 text-[#1A1A1A] leading-tight">
                      <div>{r.email || "—"}</div>
                      <div className="text-[11px] text-[#5C5C5C]">{r.phone || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-[#5C5C5C]">{r.service_name || "—"}</td>
                    <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                      {r.abandoned_email_1_skipped ? (
                        <span className="text-[#9AA3AF]">Skipped</span>
                      ) : r.abandoned_email_1_at ? (
                        <span className="text-[#0F8F4A]">{fmt(r.abandoned_email_1_at)}</span>
                      ) : (
                        <span className="text-[#9AA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                      {r.abandoned_email_2_skipped ? (
                        <span className="text-[#9AA3AF]">Skipped</span>
                      ) : r.abandoned_email_2_at ? (
                        <span className="text-[#0F8F4A]">{fmt(r.abandoned_email_2_at)}</span>
                      ) : (
                        <span className="text-[#9AA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.recovered ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E6F8EE] text-[#0F8F4A] text-[11px] font-bold uppercase tracking-wide">Yes</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF1F2] text-[#D0021B] text-[11px] font-bold uppercase tracking-wide">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => resend(r.id)}
                        disabled={busyId === r.id || !r.email}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold text-[#0466ff] hover:bg-[#F0F6FF] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`resend-${r.id}`}
                        title="Send the recovery email again"
                      >
                        <Send className="w-3 h-3" strokeWidth={2.5} /> Resend
                      </button>
                      <button
                        onClick={() => markCompleted(r.id)}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold text-[#0F8F4A] hover:bg-[#E6F8EE] disabled:opacity-40 ml-1"
                        data-testid={`mark-completed-${r.id}`}
                        title="Staff phoned/booked the customer manually — stop the scheduler chasing them"
                      >
                        <CheckSquare className="w-3 h-3" strokeWidth={2.5} /> Mark done
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function DebugPane({ config }) {
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  // Pre-fill from existing config so the admin doesn't have to look up IDs.
  const practices = config?.practices || [];
  const services = config?.services || [];
  const mappings = config?.mappings || {};

  const [practiceId, setPracticeId] = useState(practices[0]?.id || "");
  const [serviceId, setServiceId] = useState(services[0]?.id || "");
  const [date, setDate] = useState(todayStr);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const practice = practices.find((p) => p.id === practiceId);
  const service = services.find((s) => s.id === serviceId);
  const aeronaPracticeId = practice?.aerona_practice_id || "";
  const aeronaTypeId = service?.aerona_appointment_type_id || "";
  const rawMap = (mappings[practiceId] || {})[serviceId] || "";
  const clinicianIds = String(rawMap).split(",").map((s) => s.trim()).filter(Boolean);
  const [clinicianId, setClinicianId] = useState("");
  const effectiveClinician = clinicianId || clinicianIds[0] || "";

  const runDebug = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await api.adminDebugSlots({
        practice_id: aeronaPracticeId,
        appointment_type_id: aeronaTypeId,
        clinician_id: effectiveClinician,
        search_start: date,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="debug-pane">
      <h1 className="text-3xl font-extrabold mb-1" style={{ fontFamily: "Manrope" }}>Aerona Slot Debug</h1>
      <p className="text-[#5C5C5C] mb-6">
        Hits Aerona&apos;s <code>/v1/find-appointment-slots</code> directly. Bypasses our dedupe, past-slot filter,
        and whitening rules — shows exactly what Aerona is returning right now.
      </p>

      <div className="ws-card p-5 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Practice</label>
            <select value={practiceId} onChange={(e) => setPracticeId(e.target.value)} className="ws-input" data-testid="debug-practice-select">
              {practices.map((p) => <option key={p.id} value={p.id}>{p.label} (Aerona #{p.aerona_practice_id || "?"})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Service</label>
            <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setClinicianId(""); }} className="ws-input" data-testid="debug-service-select">
              {services.map((s) => <option key={s.id} value={s.id}>{s.label} (Type #{s.aerona_appointment_type_id || "?"})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Clinician ID</label>
            <input
              value={clinicianId}
              onChange={(e) => setClinicianId(e.target.value.replace(/[^\d]/g, ""))}
              placeholder={clinicianIds.length ? `Mapped: ${clinicianIds.join(", ")} — pick one` : "No mapping for this combo"}
              className="ws-input"
              data-testid="debug-clinician-input"
            />
            {clinicianIds.length > 1 && (
              <div className="text-[11px] text-[#5C5C5C] mt-1 flex flex-wrap gap-1">
                {clinicianIds.map((cid) => (
                  <button key={cid} type="button" onClick={() => setClinicianId(cid)} className="px-1.5 py-0.5 rounded border border-[#C9C9C9] hover:border-[#0466ff] text-[#1A1A1A]">
                    {cid}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C] block mb-1" style={{ fontFamily: "Manrope" }}>Search Start (YYYY-MM-DD)</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ws-input" data-testid="debug-date-input" />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={runDebug}
            disabled={loading || !aeronaPracticeId || !aeronaTypeId || !effectiveClinician}
            className="ws-btn-primary inline-flex items-center gap-2"
            data-testid="debug-fetch-button"
          >
            {loading ? "Fetching…" : "FETCH SLOTS FROM AERONA"}
          </button>
          <div className="text-xs text-[#5C5C5C]">
            Will call:&nbsp;
            <code>
              practiceId={aeronaPracticeId || "?"}, appointmentTypeId={aeronaTypeId || "?"}, clinicianId={effectiveClinician || "?"}
            </code>
          </div>
        </div>
      </div>

      {error && (
        <div className="ws-card p-5 mb-5 border-[#D0021B]" data-testid="debug-error">
          <strong className="text-[#D0021B]">Error:</strong> <span className="text-sm text-[#1A1A1A]">{error}</span>
        </div>
      )}

      {result && (
        <div className="ws-card p-5" data-testid="debug-result">
          <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm">
            <div><span className="text-[#5C5C5C]">Fetched at (UTC):</span> <strong>{result.fetched_at_utc}</strong></div>
            <div><span className="text-[#5C5C5C]">Took:</span> <strong>{result.took_ms} ms</strong></div>
            <div><span className="text-[#5C5C5C]">Slots returned:</span> <strong className="text-[#0466ff]">{result.count}</strong></div>
          </div>
          {result.slots.length === 0 ? (
            <div className="text-sm text-[#5C5C5C] py-4 text-center">Aerona returned zero slots for that combination.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[#5C5C5C] text-xs uppercase tracking-wider">
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="py-2 pr-3">Slot Start Time</th>
                    <th className="py-2 pr-3">Clinician ID</th>
                    <th className="py-2 pr-3">Other Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {result.slots.map((s, i) => {
                    const { slotStartTime, clinicianId: cid, ...rest } = s;
                    return (
                      <tr key={i} className="border-b border-[#F0F0F0]">
                        <td className="py-2 pr-3 font-mono text-[12px]">{slotStartTime}</td>
                        <td className="py-2 pr-3 font-mono text-[12px]">{cid}</td>
                        <td className="py-2 pr-3 font-mono text-[11px] text-[#5C5C5C]">{Object.keys(rest).length ? JSON.stringify(rest) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [config, setConfig] = useState({ practices: [], services: [], mappings: {} });

  // Which site's data we're viewing. On single-tenant forks the app is
  // locked to one site (via REACT_APP_SITE_LOCK) and the toggle is hidden.
  const siteLocked = isSiteLocked();
  const lockedSite = siteLocked ? getSite() : null;
  const initialSite = siteLocked
    ? lockedSite
    : (params.get("site") === "clinic" ? "clinic" : "dental");
  const [currentSite, setCurrentSite] = useState(initialSite);

  // Keep sessionStorage + URL in sync with the toggle so every API call
  // (via the api.js interceptor) fetches data for the right site.
  useEffect(() => {
    setSite(currentSite);
    const nextParams = new URLSearchParams(params);
    if (currentSite === "clinic") nextParams.set("site", "clinic");
    else nextParams.delete("site");
    if (nextParams.toString() !== params.toString()) setParams(nextParams, { replace: true });
     
  }, [currentSite]);

  useEffect(() => {
    if (!localStorage.getItem("ws_admin_token")) {
      nav("/admin");
      return;
    }
    // Refetch every tab's data whenever the site changes.
    api.adminStats().then(setStats).catch(() => nav("/admin"));
    api.adminBookings().then(setBookings).catch(() => {});
    api.adminConfig().then(setConfig).catch(() => {});
  }, [nav, currentSite]);

  const logout = () => {
    localStorage.removeItem("ws_admin_token");
    nav("/admin");
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "bookings", label: "Bookings", icon: Calendar },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "abandoned", label: "Abandoned", icon: MailWarning },
    { id: "statistics", label: "Statistics", icon: BarChart3 },
    { id: "debug", label: "Debug", icon: Bug },
    { id: "settings", label: "Settings", icon: Cog },
  ];

  const SITE_TABS = [
    { id: "dental", label: "White Smile Dental", accent: "#0466ff" },
    { id: "clinic", label: "White Smile Clinic", accent: "#5FB4F9" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="admin-dashboard">
      <aside className="w-64 bg-white border-r border-[#E5E5E5] p-6 hidden md:block">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#0466ff] grid place-items-center text-white font-bold" style={{ fontFamily: "Manrope" }}>WS</div>
          <div>
            <div className="font-extrabold text-sm" style={{ fontFamily: "Manrope" }}>Admin</div>
            <div className="text-xs text-[#5C5C5C]">{localStorage.getItem("ws_admin_email")}</div>
          </div>
        </div>
        <nav className="space-y-1">
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium flex items-center gap-3 transition-all ${
                tab === n.id ? "bg-[#0466ff] text-white" : "text-[#1A1A1A] hover:bg-[#E8F0FE]"
              }`}
              data-testid={`nav-${n.id}`}
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="mt-8 text-xs text-[#5C5C5C] flex items-center gap-2 hover:text-[#0466ff]" data-testid="logout-button">
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-6 sm:p-10 max-w-7xl">
        {/* White Smile Dental / White Smile Clinic site toggle — persists
            in the URL + sessionStorage so every admin tab loads that site's
            bookings, stats, and settings independently. Hidden entirely on
            single-tenant forks (REACT_APP_SITE_LOCK) so the admin only ever
            sees one site's data. */}
        {!siteLocked && (
          <div className="mb-6" data-testid="admin-site-toggle">
          <div className="inline-flex rounded-lg border-2 border-[#E0E0E0] bg-white overflow-hidden shadow-sm">
            {SITE_TABS.map((t) => {
              const active = currentSite === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setCurrentSite(t.id)}
                  className={`px-4 py-2 text-sm font-bold transition-colors ${
                    active ? "text-white" : "text-[#5C5C5C] hover:text-[#1A1A1A]"
                  }`}
                  style={{ fontFamily: "Manrope", backgroundColor: active ? t.accent : "transparent" }}
                  data-testid={`admin-site-tab-${t.id}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-[#5C5C5C]">
            {currentSite === "clinic"
              ? "Viewing White Smile Clinic booking site — separate services, locations, bookings and stats."
              : "Viewing White Smile Dental booking site."}
          </p>
        </div>
        )}

        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap ${tab === n.id ? "bg-[#0466ff] text-white border-[#0466ff]" : "border-[#E5E5E5]"}`}
              data-testid={`mobile-nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="overview-pane">
            <h1 className="text-3xl font-extrabold mb-1" style={{ fontFamily: "Manrope" }}>Overview</h1>
            <p className="text-[#5C5C5C] mb-6">
              Online booking activity for {currentSite === "clinic" ? "White Smile Clinic" : "White Smile Dental"}.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard label="Total bookings" value={stats?.total_bookings ?? "—"} />
              <StatCard label="Paid online" value={stats?.paid ?? "—"} />
              <StatCard label="Pending payment" value={stats?.pending_payment ?? "—"} />
              <StatCard label="Online revenue" value={`€${(stats?.online_revenue || 0).toFixed(2)}`} />
            </div>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>Recent bookings</h2>
            <div className="ws-card p-5">
              <BookingsTable bookings={bookings.slice(0, 8)} />
            </div>
          </div>
        )}
        {tab === "bookings" && (
          <div data-testid="bookings-pane">
            <h1 className="text-3xl font-extrabold mb-6" style={{ fontFamily: "Manrope" }}>All bookings</h1>
            <div className="ws-card p-5 mb-6"><BookingsByDate bookings={bookings} /></div>
            <h2 className="text-lg font-bold mb-3 text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>Everything (most recent first)</h2>
            <div className="ws-card p-5"><BookingsTable bookings={bookings} /></div>
          </div>
        )}
        {tab === "payments" && (
          <div data-testid="payments-pane">
            <h1 className="text-3xl font-extrabold mb-6" style={{ fontFamily: "Manrope" }}>Payments</h1>
            <div className="ws-card p-5"><PaymentsTable /></div>
          </div>
        )}
        {tab === "statistics" && <StatisticsPane bookings={bookings} />}
        {tab === "debug" && <DebugPane config={config} />}
        {tab === "abandoned" && <AbandonedPane siteKey={currentSite} />}
        {tab === "settings" && (
          <div data-testid="settings-pane">
            <AeronaIntegratorSettings site={currentSite} />
          </div>
        )}
      </main>
    </div>
  );
}
